import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { loadProveedorCatalogs } from "../utils/catalogs";
import { camposCategorias } from "../utils/proveedores";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { showAlert } from "../alertService";
import {
  PlusIcon,
  XMarkIcon,
  BriefcaseIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const normalizeInstagramHandle = (value) => {
  if (!value) return "";
  return value
    .trim()
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "")
    .replace(/[/?#].*$/, "")
    .replace(/^@+/, "")
    .trim();
};

const ProveedorCreateDialog = ({
  open,
  onClose,
  onCreated,
  initialCategoria = "",
}) => {
  const { selectedVenueId } = useVenue();
  const { userInfo, user } = useAuth();

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [nombre, setNombre] = useState("");
  // Una fila por categoría, sin principal (utils/proveedores.js).
  const [filas, setFilas] = useState(() => [
    { categoria: initialCategoria || "", subcategoria: "" },
  ]);
  const [contactoNombre, setContactoNombre] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");
  const [contactoTelefono, setContactoTelefono] = useState("");
  const [instagram, setInstagram] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCatLoading(true);
    loadProveedorCatalogs()
      .then((data) => {
        if (cancelled) return;
        setCategorias(data.categorias.filter((c) => c.activo !== false));
        setSubcategorias(data.subcategorias.filter((s) => s.activo !== false));
      })
      .catch((e) => {
        console.error(e);
        showAlert("Error al cargar categorías: " + e.message, "error");
      })
      .finally(() => {
        if (!cancelled) setCatLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const filaInicial = [{ categoria: initialCategoria || "", subcategoria: "" }];
    if (open) {
      setFilas(filaInicial);
    } else {
      setNombre("");
      setFilas(filaInicial);
      setContactoNombre("");
      setContactoEmail("");
      setContactoTelefono("");
      setInstagram("");
    }
  }, [open, initialCategoria]);

  const subcategoriasPorCategoria = useMemo(() => {
    const m = {};
    subcategorias.forEach((s) => {
      (m[s.parentId] || (m[s.parentId] = [])).push(s);
    });
    return m;
  }, [subcategorias]);

  const setFila = (idx, patch) =>
    setFilas((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const quitarFila = (idx) => setFilas((prev) => prev.filter((_, i) => i !== idx));
  const agregarFila = () => setFilas((prev) => [...prev, { categoria: "", subcategoria: "" }]);
  const ultimaFilaVacia = !filas[filas.length - 1]?.categoria;

  const canSubmit =
    nombre.trim().length > 0 && !!filas[0]?.categoria && !saving && !catLoading;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedVenueId) return;
    setSaving(true);
    try {
      const contactos = [];
      if (
        contactoNombre.trim() ||
        contactoEmail.trim() ||
        contactoTelefono.trim()
      ) {
        contactos.push({
          id:
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `c_${Date.now()}`,
          nombre: contactoNombre.trim(),
          rol: null,
          email: contactoEmail.trim() || null,
          telefono: contactoTelefono.trim() || null,
          esPrincipal: true,
          notas: null,
        });
      }

      const instagramHandle = normalizeInstagramHandle(instagram);

      const proveedorData = {
        nombre: nombre.trim(),
        // categoria/subcategoria (principal), categorias e isPlanner.
        ...camposCategorias(filas),
        userId: null,
        razonSocial: null,
        rfc: null,
        website: null,
        instagram: instagramHandle || null,
        direccion: null,
        status: "activo",
        tags: [],
        notasInternas: "",
        contactos,
        ratingPromedio: null,
        ratingCount: 0,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      };

      const proveedoresCol = collection(
        db,
        "venues",
        selectedVenueId,
        "proveedores"
      );
      const docRef = await addDoc(proveedoresCol, proveedorData);

      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_CREATE,
          entity: "proveedor",
          entityId: docRef.id,
          entityName: proveedorData.nombre,
          details: {
            categoria: proveedorData.categoria,
            subcategoria: proveedorData.subcategoria,
            ...(proveedorData.categorias.length > 1
              ? { categorias: proveedorData.categorias.map((c) => c.categoria) }
              : {}),
          },
        },
        userInfo,
        user
      );

      showAlert("Proveedor creado", "success");
      if (onCreated) {
        onCreated(docRef.id, {
          id: docRef.id,
          nombre: proveedorData.nombre,
          categoria: proveedorData.categoria,
          subcategoria: proveedorData.subcategoria,
          categorias: proveedorData.categorias,
          isPlanner: proveedorData.isPlanner,
        });
      }
      onClose?.();
    } catch (e) {
      console.error(e);
      showAlert("Error al crear proveedor: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <BriefcaseIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              Nuevo proveedor
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              autoFocus
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Decoraciones García"
              className={inputClass}
            />
          </div>

          {/* Categorías: una fila por categoría, todas pesan igual. Las ya
              elegidas en otras filas no se vuelven a ofrecer. */}
          <div className="space-y-3">
            {filas.map((fila, idx) => {
              const subsDeFila = subcategoriasPorCategoria[fila.categoria] || [];
              const usadas = new Set(
                filas.filter((_, i) => i !== idx).map((f) => f.categoria).filter(Boolean)
              );
              return (
                <div
                  key={idx}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 sm:gap-3 items-end"
                >
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      {idx === 0 ? (
                        <>
                          Categoría <span className="text-red-500">*</span>
                        </>
                      ) : (
                        `Categoría ${idx + 1}`
                      )}
                    </label>
                    <select
                      value={fila.categoria}
                      onChange={(e) =>
                        setFila(idx, { categoria: e.target.value, subcategoria: "" })
                      }
                      disabled={catLoading}
                      className={inputClass}
                    >
                      <option value="">— Selecciona —</option>
                      {categorias
                        .filter((c) => !usadas.has(c.id))
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      Subcategoría
                    </label>
                    <select
                      value={fila.subcategoria}
                      onChange={(e) => setFila(idx, { subcategoria: e.target.value })}
                      disabled={!fila.categoria || subsDeFila.length === 0}
                      className={inputClass + " disabled:opacity-50"}
                    >
                      <option value="">— Opcional —</option>
                      {subsDeFila.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  {filas.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => quitarFila(idx)}
                      title="Quitar categoría"
                      className="h-[38px] w-[38px] shrink-0 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="hidden sm:block w-[38px]" />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={agregarFila}
              disabled={ultimaFilaVacia || catLoading}
              className="inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-40"
              style={{ color: ACCENT }}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Agregar otra categoría
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Instagram (opcional)
            </label>
            <div className="flex items-center gap-2">
              <span className="shrink-0 rounded-lg bg-zinc-100 dark:bg-zinc-800 px-2.5 py-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
                @
              </span>
              <input
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="tu_handle"
                className={inputClass}
              />
            </div>
          </div>

          <div className="border-t border-zinc-200 dark:border-zinc-700/60 pt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
              Contacto inicial (opcional)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={contactoNombre}
                onChange={(e) => setContactoNombre(e.target.value)}
                placeholder="Nombre"
                className={inputClass}
              />
              <input
                type="email"
                value={contactoEmail}
                onChange={(e) => setContactoEmail(e.target.value)}
                placeholder="Email"
                className={inputClass}
              />
              <input
                type="tel"
                value={contactoTelefono}
                onChange={(e) => setContactoTelefono(e.target.value)}
                placeholder="Teléfono"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            {saving ? "Creando..." : "Crear proveedor"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProveedorCreateDialog;
